import { compose } from 'redux';
import { memo, useMemo } from 'react';
import ExpandMore from '@material-ui/icons/ExpandMore';
import ExpandLess from '@material-ui/icons/ExpandLess';
import withStyles from '@material-ui/styles/withStyles';
import { Link } from 'react-router-dom';
import { IconButton, Typography } from '@material-ui/core';

import { Table, TableRowActionsCell, TableRow } from 'altus-ui-components';

import { formatDate } from 'utils/format.util';
import { Format, EMPTY_LIST } from 'app/app.constants';
import { toField, toWellbore } from 'utils/route.util';

const WellboresTable = ({
  classes,
  wellboresByField = EMPTY_LIST,
  onMouseEnter,
  onMouseLeave,
  ...rest
}) => {
  const columns = useMemo(
    () => [
      {
        id: 'placeholder',
        Header: <TableRowActionsCell minItems={1} />,
        Cell: <TableRowActionsCell minItems={1} />,
      },
      {
        xs: 6,
        id: 'name',
        Header: 'Name',
        accessor: (wellOrField) => wellOrField.get('name'),
        Cell: ({ row, value }) => {
          const isField = row.original.get('isField');

          if (isField) {
            return (
              <Link
                to={toField(
                  row.original?.get('fieldId'),
                  row.original?.get('id'),
                )}
                className={classes.wellHeader}
              >
                <Typography variant="h6">{value}</Typography>
              </Link>
            );
          }

          return value;
        },
      },
      {
        xs: 6,
        Header: 'Survey',
        id: 'trajectoryUpdated',
        accessor: (wellOrField) => wellOrField.get('trajectoryUpdated'),
        Cell: ({ value }) => {
          if (!value) {
            return null;
          }

          return (
            <Typography noWrap variant="body2">
              <Typography
                variant="inherit"
                component="span"
                className={classes.surveyUploadedDescription}
              >
                Survey uploaded
              </Typography>
              &nbsp;
              <Typography variant="inherit" component="span">
                {formatDate(value, Format.date)}
              </Typography>
            </Typography>
          );
        },
      },
      {
        id: 'expander',
        Cell: ({ row }) => {
          if (!row.canExpand) return null;

          return (
            <IconButton
              {...row.getToggleRowExpandedProps()}
              title="Toggle wells"
            >
              {row.isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          );
        },
      },
    ],
    [classes],
  );

  const WellboreTableRow = ({ item, ...rest }) => {
    const isWell = item.get('isWell');
    const isField = item.get('isField');

    if (isField) {
      return (
        <TableRow
          {...rest}
          item={item}
          classes={{
            root: classes.fieldRoot,
          }}
        />
      );
    }

    if (isWell) {
      return (
        <TableRow
          {...rest}
          item={item}
          classes={{
            root: classes.wellRoot,
          }}
        />
      );
    }

    return (
      <Link to={toWellbore(item.get('fieldId'), item.get('id'))}>
        <TableRow
          {...rest}
          item={item}
          title="Open well"
          onMouseEnter={() => onMouseEnter(item.toJS())}
          onMouseLeave={onMouseLeave}
        />
      </Link>
    );
  };

  return (
    <Table
      useExpanded
      usePagination
      subrowOffset={3}
      manualPagination
      columns={columns}
      useGlobalFilter={false}
      items={wellboresByField}
      paginateExpandedRows={false}
      TableRowComponent={WellboreTableRow}
      noItemsMessage="No matching wells..."
      {...rest}
    />
  );
};

const styles = (theme) => ({
  surveyUploadedDescription: {
    color: theme.palette.secondary.mainLight,
  },
  wellHeader: {
    color: theme.palette.common.white,
  },
  wellRoot: {
    '&:hover': {
      background: theme.altus.components.TableRow.background,
    },
  },
  fieldRoot: {
    top: 0,
    position: 'sticky',
    background: theme.altus.components.TableRow.hoverColor,
    '&:hover': {
      background: theme.altus.components.TableRow.hoverColor,
    },
  },
});

export default compose(withStyles(styles), memo)(WellboresTable);
